import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";

type LogRow = {
  id: string;
  adminEmail: string;
  targetName: string;
  createdAt: string;
};

const PAGE_SIZE = 50;

export function AdminAccessLogs() {
  const { refresh } = useAuth();
  const [logs, setLogs] = useState<LogRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync(
        `/api/admin/impersonation-logs?limit=${PAGE_SIZE}&offset=${offset}`,
        { credentials: "include" },
        refresh,
      );
      if (cancelled) return;
      if (!r.ok) return;
      const d = (await r.json()) as { logs: LogRow[]; total: number };
      setLogs(d.logs);
      setTotal(d.total);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, offset]);

  if (!logs) {
    return (
      <AdminPageShell title="Historial de accesos">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminPageShell
      title="Historial de accesos"
      description="Registro de cada vez que un superadmin ingresó a una cuenta de cliente usando 'Ingresar como', para trazabilidad y soporte."
    >
      {logs.length === 0 ? (
        <EmptyState
          title="Sin accesos registrados"
          description="Cuando un superadmin ingrese a una cuenta desde Planes y cuentas, cada acceso quedará listado acá."
          icon="🔑"
        />
      ) : (
        <>
          <div
            className={css({
              overflowX: "auto",
              rounded: "xl",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
              bg: "bg.card",
            })}
          >
            <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
              <thead>
                <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", whiteSpace: "nowrap" })}>
                    Fecha y hora
                  </th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Admin</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>
                    Cuenta accedida
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                    <td className={css({ py: "3", px: "4", color: "fg.muted", whiteSpace: "nowrap", fontSize: "xs" })}>
                      {format(new Date(l.createdAt), "d MMM yyyy HH:mm", { locale: es })}
                    </td>
                    <td className={css({ py: "3", px: "4", fontSize: "xs" })}>{l.adminEmail}</td>
                    <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "medium" })}>{l.targetName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              mt: "4",
            })}
          >
            <p className={css({ fontSize: "xs", color: "fg.muted" })}>
              Página {page} de {totalPages} · {total} accesos en total
            </p>
            <div className={css({ display: "flex", gap: "2" })}>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Anterior
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      )}
    </AdminPageShell>
  );
}
